/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import Container from "../components/Container";
import NavigationHeader from "../components/NavigationHeader";
import LoadingIndicator from "../components/LoadingIndicator";
import Text from "../components/ui/Text";
import T from "../components/ui/Translation";
import Badge from "../components/ui/Badge";
import MembershipItem from "../components/membership/MembershipItem";
import { useGetMemberships } from "../lib/react-query/useGetMemberships";
import { useServices } from "../lib/react-query/useServices";
import usePluginUtils from "../hooks/usePluginUtils";
import { ROUTES } from "../constants/constants";
import Flex from "../components/ui/Flex";
import Stack from "../components/ui/Stack";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  ${({ theme }) => theme.containerMedia.sm} {
    grid-template-columns: 1fr;
  }
`;


const ChooseMembershipPage = () => {
  const [searchParams] = useSearchParams();
  const sourceServiceId = +searchParams.get("service") || null;
  const navigate = useNavigate();
  const { isPluginEnabled } = usePluginUtils();
  const membershipPluginEnabled = isPluginEnabled("membership");
  const memberships = useGetMemberships({}, { enabled: membershipPluginEnabled });
  const { data: services } = useServices(undefined, { enabled: membershipPluginEnabled });

  useEffect(() => {
    if (!membershipPluginEnabled) navigate(ROUTES.HOME ?? "/", { replace: true });
  }, [membershipPluginEnabled, navigate]);

  const servicesById = useMemo(() => {
    if (!Array.isArray(services)) return {};
    return services.reduce((acc, s) => { acc[+s.id] = s; return acc; }, {});
  }, [services]);

  const sourceService = sourceServiceId ? servicesById[sourceServiceId] : null;

  const filtered = useMemo(() => {
    const list = memberships.data?.data;

    if (!Array.isArray(list)) return [];
    if (!sourceServiceId) return list;

    return list.filter((m) =>
      Array.isArray(m.services) && m.services.some((sid) => +sid === sourceServiceId)
    );
  }, [memberships.data, sourceServiceId]);

  const reserveDescription = filtered.some((m) => m.description);

  if (!membershipPluginEnabled) return null;

  if (memberships.isLoading) {
    return (
      <Container>
        <NavigationHeader to={-1} back={-1} title={<T>choose_membership</T>} cart clientLogin />
        <LoadingIndicator page />
      </Container>
    );
  }

  return (
    <Container>
      <NavigationHeader to={-1} back={-1} title="Choose membership" cart clientLogin />
      <Stack $gap="0.5rem" $align="start">
        <Flex><Text color="dark">Memberships that include this service</Text></Flex>
        {sourceService && <Badge color="neutral" title={sourceService.name} />}
        <Grid>
          {filtered.map((m) => (
            <MembershipItem
              key={m.id}
              membership={m}
              highlightServiceId={sourceServiceId}
              reserveDescription={reserveDescription}
            />
          ))}
        </Grid>
      </Stack>
    </Container>
  );
};

export default ChooseMembershipPage;
